import { useMemo } from 'react';
import useBookingBill from '@/features/dashboard/bookingChanges/hooks/useBookingBill.ts';
import useGetOpenDraft from '@/features/dashboard/bookingChanges/hooks/useGetOpenDraft.ts';
import useChangePreview from '@/features/dashboard/bookingChanges/hooks/useChangePreview.ts';
import useGetAffectedConsents from '@/features/dashboard/bookingChanges/hooks/useGetAffectedConsents.ts';
import { bookingChangeDetailMapper } from '@/features/dashboard/bookingChanges/types/BookingChangeDetailDto.mapper.ts';
import { ClientAcceptanceVm } from '@/features/dashboard/bookingChanges/types/ChangePackageFlowViewModel.ts';

/**
 * Krok W3 — ta sama paczka widziana przez osobę zapisującą.
 *
 * Czyta **dane operatora**, a nie publiczny odczyt paczki: token rezerwacji
 * powstaje wyłącznie w szablonie wiadomości, więc panel operatora nie ma czym
 * zawołać `GET …/booking-summary/change`. Wszystkie cztery zapytania stoją już
 * w pamięci podręcznej po krokach W1 i W2 — paczka wysłana klientowi ma status
 * `PENDING_CLIENT`, czyli wciąż otwarty, a podgląd kwot działa dla obu stanów
 * otwartych.
 */
const useClientAcceptanceView = (bookingId: string | undefined) => {
  const billQuery = useBookingBill(bookingId);
  const changeQuery = useGetOpenDraft(bookingId);
  const previewQuery = useChangePreview(bookingId);
  const consentsQuery = useGetAffectedConsents(billQuery.data?.tourId);

  const view: ClientAcceptanceVm | null = useMemo(() => {
    const change = changeQuery.data;
    if (!billQuery.data || !change || !consentsQuery.data) {
      return null;
    }
    return bookingChangeDetailMapper.toClientAcceptanceVm(
      change,
      billQuery.data,
      consentsQuery.data,
      previewQuery.preview,
    );
  }, [
    billQuery.data,
    changeQuery.data,
    consentsQuery.data,
    previewQuery.preview,
  ]);

  return {
    view,
    change: changeQuery.data ?? null,
    isLoading:
      billQuery.isLoading ||
      changeQuery.isLoading ||
      consentsQuery.isLoading ||
      previewQuery.isLoading,
    isError: billQuery.isError || changeQuery.isError || consentsQuery.isError,
    /** Odmowa podglądu — kwot cząstkowych nie znamy, więc ekran mówi to wprost. */
    error: previewQuery.error,
  };
};

export default useClientAcceptanceView;
